import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import React, { useMemo, useState } from 'react';
import { styles } from './SelectStoryTypeModal.styles';
import { ISetupPdfMessages } from '../../../../../types/setupPDFTypes';

export type SelectStoryTypeModalProps = {
  messages: ISetupPdfMessages;
  onClose: () => void;
  onContinue: (storiesSelected: StoryState[]) => void;
};

export enum StoryState {
  PUBLIC = 'published',
  INTERNAL = 'internalPublished',
  DRAFT = 'draft'
}

export const getStoryTypeOptions = (messages: ISetupPdfMessages): { value: StoryState; label: string }[] => [
  {
    value: StoryState.PUBLIC,
    label: messages.allPublicStories
  },
  {
    value: StoryState.INTERNAL,
    label: messages.allInternalStories
  },
  {
    value: StoryState.DRAFT,
    label: messages.allDraftStories
  }
];

export const SelectStoryTypeModal = ({ onClose, onContinue, messages }: SelectStoryTypeModalProps) => {
  const [selectedStoryTypes, setSelectedStoryTypes] = useState<StoryState[]>([StoryState.PUBLIC]);
  const storyTypeOptions = useMemo(() => getStoryTypeOptions(messages), [messages]);
  const handleStoryTypeChange = (storyType: StoryState) => () => {
    setSelectedStoryTypes((prev) =>
      prev.includes(storyType) ? prev.filter((type) => type !== storyType) : [...prev, storyType]
    );
  };

  return (
    <Dialog
      data-test="selectStoryTypeModal"
      open={true}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
      fullWidth={true}
      maxWidth="sm"
    >
      <Box sx={styles.dialogHeader}>
        <DialogTitle>{messages.setupPdf}</DialogTitle>
        <IconButton className="MuiDialog__close-button" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={styles.dialogContent}>
        <Typography>{messages.selectStories}</Typography>

        {storyTypeOptions.map((option) => (
          <FormControlLabel
            key={option.value}
            control={
              <Checkbox
                checked={selectedStoryTypes.includes(option.value)}
                onChange={handleStoryTypeChange(option.value)}
              />
            }
            label={option.label}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="secondary" onClick={onClose}>
          {messages.cancel}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedStoryTypes.length}
          onClick={() => {
            onContinue(selectedStoryTypes);
          }}
        >
          {messages.continue}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
